import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "../Layout/Sidebar";
import Header from "../Layout/Header";
import Swal from "sweetalert2";
import axios from "axios";
import "./ProjectCandidate.css";
import ProjectTabs from "./ProjectTabs";
import { API_BASE } from "../../../ApiUrl";

const TENANT_RECORDS_API_URL = `${API_BASE}/tenant-records/`;
const PROFESSIONAL_PROFILE_API_URL = `${API_BASE}/professional-profiles/`;
const PROJECTS_API_URL = `${API_BASE}/projects/`;

const ProjectCandidateList = () => {
  const navigate = useNavigate();

  const [candidates, setCandidates] = useState([]);
  const [professionalsMap, setProfessionalsMap] = useState({});
  const [projectsMap, setProjectsMap] = useState({});
  const [loading, setLoading] = useState(false);

  const getTenantUser = () => {
    try {
      const tenantUser = localStorage.getItem("tenant_user");
      return tenantUser ? JSON.parse(tenantUser) : null;
    } catch (error) {
      console.error("Error reading tenant_user:", error);
      return null;
    }
  };

  useEffect(() => {
    const fetchAll = async () => {
      const tenantUser = getTenantUser();

      if (!tenantUser?.tenant_id) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);

        const res = await axios.post(
          TENANT_RECORDS_API_URL,
          {
            tenant_id: tenantUser.tenant_id,
            tables: ["project_candidates"]
          },
          { headers: { "Content-Type": "application/json" } }
        );

        const result = res.data;

        let rows = [];
        if (result?.data?.project_candidates) {
          rows = result.data.project_candidates;
        } else if (result?.project_candidates) {
          rows = result.project_candidates;
        } else if (Array.isArray(result?.data)) {
          rows = result.data;
        } else if (Array.isArray(result)) {
          rows = result;
        }

        setCandidates(rows);

        const uniqueProfessionalIds = [
          ...new Set(
            rows
              .map((c) => c.professional)
              .filter((id) => id !== null && id !== undefined)
          )
        ];

        const uniqueProjectIds = [
          ...new Set(
            rows
              .map((c) => c.project)
              .filter((id) => id !== null && id !== undefined)
          )
        ];

        const professionalResults = await Promise.all(
          uniqueProfessionalIds.map(async (id) => {
            try {
              const profileRes = await axios.get(
                `${PROFESSIONAL_PROFILE_API_URL}${id}/`
              );
              const profileData =
                profileRes.data?.data || profileRes.data || null;
              return [id, profileData];
            } catch (err) {
              console.error(`Error fetching professional ${id}:`, err);
              return [id, null];
            }
          })
        );

        const pMap = {};
        professionalResults.forEach(([id, profile]) => {
          pMap[id] = profile;
        });
        setProfessionalsMap(pMap);

        const projectResults = await Promise.all(
          uniqueProjectIds.map(async (id) => {
            try {
              const projectRes = await axios.get(`${PROJECTS_API_URL}${id}/`);
              const projectData =
                projectRes.data?.data || projectRes.data || null;
              return [id, projectData];
            } catch (err) {
              console.error(`Error fetching project ${id}:`, err);
              return [id, null];
            }
          })
        );

        const prMap = {};
        projectResults.forEach(([id, project]) => {
          prMap[id] = project;
        });
        setProjectsMap(prMap);
      } catch (error) {
        console.error("Error fetching project candidates:", error);
        Swal.fire({
          icon: "error",
          title: "Unable to Load Candidates",
          text: "Could not fetch project candidates."
        });
        setCandidates([]);
      } finally {
        setLoading(false);
      }
    };

    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getProfessionalName = (id) => {
    const p = professionalsMap[id];
    if (!p) return `Professional ${id}`;
    const first = p.first_name || "";
    const last = p.last_name || "";
    const full = `${first} ${last}`.trim();
    return full || p.full_name || `Professional ${id}`;
  };

  const getProfessionalEmail = (id) => {
    const p = professionalsMap[id];
    return p?.email || "—";
  };

  const getProfessionalPhone = (id) => {
    const p = professionalsMap[id];
    return p?.phone || p?.mobile_number || "—";
  };

  const getProjectName = (id) => {
    const p = projectsMap[id];
    if (!p) return `Project ${id}`;
    return p.project_name || p.name || `Project ${id}`;
  };

  const getProjectCode = (id) => {
    const p = projectsMap[id];
    return p?.project_code || "—";
  };

  const handleView = (candidate) => {
    navigate(`/admin/project-candidates/${candidate.id}`, {
      state: { candidate }
    });
  };

  const formatStatus = (status) => {
    if (!status) return "—";
    return status
      .toString()
      .toLowerCase()
      .replace(/_/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase());
  };

  const getStatusClass = (status) => {
    switch (status) {
      case "SHORTLISTED":
        return "tenant-membership-status tenant-membership-status--shortlisted";
      case "UNDER_REVIEW":
        return "tenant-membership-status tenant-membership-status--review";
      case "REJECTED":
        return "tenant-membership-status tenant-membership-status--rejected";
      case "SELECTED":
        return "tenant-membership-status tenant-membership-status--selected";
      default:
        return "tenant-membership-status";
    }
  };

  return (
    <div className="app-layout">
      <Sidebar />

      <div className="main-content">
        <Header />

        <div className="tenant-membership-page">
          <ProjectTabs />

          <div className="tenant-membership-page-header">
            <div>
              <h2>Project Candidates</h2>
            </div>
          </div>

          <div className="tenant-membership-list-card">
            {loading ? (
              <div className="tenant-membership-loading">
                Loading candidates...
              </div>
            ) : candidates.length === 0 ? (
              <div className="tenant-membership-empty">
                <div className="tenant-membership-empty-icon">+</div>
                <h3>No Candidates Found</h3>
                <p>
                  There are no project candidates available for the current
                  tenant.
                </p>
              </div>
            ) : (
              <div className="tenant-membership-table-wrapper">
                <table className="tenant-membership-table">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Project</th>
                      <th>Professional</th>
                      
                      <th>Shortlist Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>

                  <tbody>
                    {candidates.map((c) => (
                      <tr key={c.id}>
                        <td className="tenant-membership-muted">
                          #{c.id}
                        </td>

                        <td>
                          <strong>{getProjectName(c.project)}</strong>
                          {getProjectCode(c.project) !== "—" && (
                            <div className="tenant-membership-muted">
                              {getProjectCode(c.project)}
                            </div>
                          )}
                        </td>

                        <td>
                          <strong>
                            {getProfessionalName(c.professional)}
                          </strong>
                        </td>

                        

                        <td>
                          <span className={getStatusClass(c.shortlist_status)}>
                            {formatStatus(c.shortlist_status)}
                          </span>
                        </td>

                        <td>
                          <div className="tenant-membership-actions">
                            <button
                              type="button"
                              className="tenant-membership-edit-btn"
                              onClick={() => handleView(c)}
                            >
                              View
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectCandidateList;